import React, { useEffect } from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../app/store';
import { GoalAggregationType, GoalSourceType, GoalTargetType, ValueResponse, ValueType } from '../../app/types';
import { listChecklists, selectAllChecklists } from '../checklists/checklistsSlice';
import ValueTypeInput from '../inputs/ValueTypeInput';
import { listMeasurements, selectAllMeasurements } from '../measurements/measurementsSlice';
import { useAuth } from '../signin/auth';
import { Goal, GoalUpdate } from './types';

type NumericFormType = number | '';

interface EditGoalProps {
  goal?: Goal;
  goalState: GoalUpdate;
  cloneGoal?: boolean;
  setGoalState: React.Dispatch<React.SetStateAction<GoalUpdate>>;
  children?: JSX.Element;
};

EditGoal.defaultProps = {
  cloneGoal: false,
};

function EditGoal(props: EditGoalProps) {
  const { goal, goalState, cloneGoal, children, setGoalState } = props;
  const measurements = useSelector(selectAllMeasurements);
  const checklists = useSelector(selectAllChecklists);
  const auth = useAuth();
  const dispatch = useDispatch<AppDispatch>();

  const convertValueResponseToNumericFormType = (v: ValueResponse | undefined): NumericFormType => {
    if (v !== null && v !== undefined) {
      switch (typeof v) {
        case 'boolean':
          return v ? 1 : 0;
        default:
          return v;
      }
    }
    return '';
  };

  const convertFormValueToNumericResponse = (v: string): ValueResponse => {
    return v === '' ? null : parseFloat(v);
  }

  type ChangeEvent = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  const onNameChanged: ChangeEvent = e => setGoalState({ ...goalState, name: e.target.value });
  const onStartDateChanged: ChangeEvent = e => setGoalState({ ...goalState, startDate: parseInt(e.target.value) });
  const onEndDateChanged: ChangeEvent = e => setGoalState({ ...goalState, endDate: parseInt(e.target.value) });
  const onValueTypeChanged = (v: ValueType) => setGoalState({ ...goalState, type: v });
  const onAggregationTypeChanged: ChangeEvent = e => setGoalState({ ...goalState, aggregationType: parseInt(e.target.value) });
  const onTargetTypeChanged: ChangeEvent = e => setGoalState({ ...goalState, targetType: parseInt(e.target.value) });
  const onSourceTypeChanged: ChangeEvent = e => setGoalState({ ...goalState, sourceType: parseInt(e.target.value) });
  const onMeasurementChanged: ChangeEvent = e => setGoalState({ ...goalState, measurementID: e.target.value });
  const onChecklistChanged: ChangeEvent = e => setGoalState({ ...goalState, checklistID: e.target.value });
  // TODO: Handle int / float / boolean? targets.
  const onTargetValueAChanged: ChangeEvent = e => setGoalState({ ...goalState, targetValueA: convertFormValueToNumericResponse(e.target.value) });
  const onTargetValueBChanged: ChangeEvent = e => setGoalState({ ...goalState, targetValueB: convertFormValueToNumericResponse(e.target.value) });

  useEffect(() => {
    const getDate = (date?: Date): number => {
      if (!date) {
        date = new Date();
      }
      return (date.getFullYear() * 10000) + ((date.getMonth() + 1) * 100) + date.getDate();
    };

    const getEndOfYearDate = (): number => {
      const date = new Date();
      date.setMonth(11);
      date.setDate(31);
      return getDate(date);
    }

    let updatedGoalState: GoalUpdate = {
      startDate: getDate(),
      endDate: getEndOfYearDate(),
      ...goal,
    };

    if (cloneGoal) {
      delete updatedGoalState.id;
      if (updatedGoalState.name && updatedGoalState.name.length > 0) {
        updatedGoalState.name = `Copy of ${updatedGoalState.name}`;
      }
    }

    setGoalState(updatedGoalState);
  }, [goal, cloneGoal, setGoalState]);

  useEffect(() => {
    if (auth.authToken) {
      dispatch(listChecklists({ authToken: auth.authToken }));
      dispatch(listMeasurements({ authToken: auth.authToken }));
    }
  }, [dispatch, auth.authToken]);

  return (
    <Form>
      {goalState.id && (
        <Form.Group as={Row} controlId="goalID" className="mb-3">
          <Form.Label column sm="2">ID</Form.Label>
          <Col sm="10">
            <p><code>{goalState.id}</code></p>
          </Col>
        </Form.Group>
      )}
      <Form.Group as={Row} controlId="goalName" className="mb-3">
        <Form.Label column sm="2">Name</Form.Label>
        <Col sm="10">
          <Form.Control type="text" placeholder="" value={goalState.name || ''} onChange={onNameChanged} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalStartDate" className="mb-3">
        <Form.Label column sm="2">Start Date</Form.Label>
        <Col sm="10">
          <Form.Control type="number" placeholder="" value={goalState.startDate || ''} onChange={onStartDateChanged} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalEndDate" className="mb-3">
        <Form.Label column sm="2">End Date</Form.Label>
        <Col sm="10">
          <Form.Control type="number" placeholder="" value={goalState.endDate || ''} onChange={onEndDateChanged} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalType" className="mb-3">
        <Form.Label column sm="2">Type</Form.Label>
        <Col sm="10">
          <ValueTypeInput value={goalState.type ? goalState.type : ValueType.Integer} onChange={onValueTypeChanged} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalAggregationType" className="mb-3">
        <Form.Label column sm="2">Aggregation Type</Form.Label>
        <Col sm="10">
          <Row>
            <Col md={4}>
              <Form.Select value={goalState.aggregationType === null ? '' : goalState.aggregationType} onChange={onAggregationTypeChanged} className="col-md-4">
                <option value={GoalAggregationType.Identity}>No aggregation</option>
                <option value={GoalAggregationType.Sum}>Sum values</option>
                <option value={GoalAggregationType.Min}>Minimum value</option>
                <option value={GoalAggregationType.Max}>Maximum value</option>
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col>
              {goalState.aggregationType === GoalAggregationType.Identity &&
                <small className="form-text text-muted">The chart shows each day's value as it was input. Example: tracking daily weight.</small>
              }
              {goalState.aggregationType === GoalAggregationType.Sum &&
                <small className="form-text text-muted">The chart shows the total sum of all values over the goal's time range.  Example: tracking total exercise days over time.</small>
              }
              {goalState.aggregationType === GoalAggregationType.Min &&
                <small className="form-text text-muted">The chart shows the minimum value entered up to that date.</small>
              }
              {goalState.aggregationType === GoalAggregationType.Max &&
                <small className="form-text text-muted">The chart shows the maximum value entered up to that date.</small>
              }
            </Col>
          </Row>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalTargetType" className="mb-3">
        <Form.Label column sm="2">Target Type</Form.Label>
        <Col sm="10">
          <Row>
            <Col md={4}>
              <Form.Select value={goalState.targetType === null ? '' : goalState.targetType} onChange={onTargetTypeChanged} className="col-md-4">
                <option value={GoalTargetType.Linear}>Linear</option>
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col>
              {goalState.targetType === GoalTargetType.Linear &&
                <small className="form-text text-muted">The goal is linearly interpolated from value A to value B.</small>
              }
            </Col>
          </Row>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalTargetValueA" className="mb-3">
        {/* TODO: Switch input types depending on goal value. */}
        <Form.Label column sm="2">Target Value A</Form.Label>
        <Col sm="10">
          <Form.Control type="number" placeholder="" value={convertValueResponseToNumericFormType(goalState.targetValueA)} onChange={onTargetValueAChanged} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalTargetValueB" className="mb-3">
        {/* TODO: Switch input types depending on goal value. */}
        <Form.Label column sm="2">Target Value B</Form.Label>
        <Col sm="10">
          <Form.Control type="number" placeholder="" value={convertValueResponseToNumericFormType(goalState.targetValueB)} onChange={onTargetValueBChanged} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="goalSourceType" className="mb-3">
        <Form.Label column sm="2">Source Type</Form.Label>
        <Col sm="10">
          <Row>
            <Col md={4}>
              <Form.Select value={goalState.sourceType === null ? '' : goalState.sourceType} onChange={onSourceTypeChanged} className="col-md-4">
                <option value={GoalSourceType.MeasurementSource}>Measurement</option>
                <option value={GoalSourceType.ChecklistSource}>Checklist</option>
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col>
              {(goalState.sourceType === GoalSourceType.MeasurementSource || !goalState.sourceType) &&
                <small className="form-text text-muted">The goal is driven by measurements. Example: a daily reading goal.</small>
              }
              {goalState.sourceType === GoalSourceType.ChecklistSource &&
                <small className="form-text text-muted">The goal is driven by a checklist.  Example: a list of movies to watch.</small>
              }
            </Col>
          </Row>
        </Col>
      </Form.Group>

      {(goalState.sourceType === GoalSourceType.MeasurementSource || !goalState.sourceType) && (
        <Form.Group as={Row} controlId="measurement" className="mb-3">
          <Form.Label column sm="2">Measurement</Form.Label>
          <Col sm="10">
            <Form.Select value={goalState.measurementID || ''} onChange={onMeasurementChanged}>
              <option key="null" value="">Not set</option>
              {measurements.map(m =>
                <option key={m.id} value={m.id}>{m.name}</option>
              )}
            </Form.Select>
          </Col>
        </Form.Group>
      )}
      {goalState.sourceType === GoalSourceType.ChecklistSource && (
        <Form.Group as={Row} controlId="checklist" className="mb-3">
          <Form.Label column sm="2">Checklist</Form.Label>
          <Col sm="10">
            <Form.Select value={goalState.checklistID || ''} onChange={onChecklistChanged}>
              <option key="null" value="">Not set</option>
              {checklists.map(m =>
                <option key={m.id} value={m.id}>{m.name}</option>
              )}
            </Form.Select>
          </Col>
        </Form.Group>
      )}
      {children}
    </Form>
  );
};

export default EditGoal;